export type ArtworkBackgroundRemovalMode = 'preserve_alpha' | 'clean_light' | 'adaptive_paper' | 'line_extraction';

export interface ArtworkBackgroundRemovalResult {
  mode: ArtworkBackgroundRemovalMode;
  neutralLightRatio: number;
}

export interface ArtworkBackgroundAnalysis extends ArtworkBackgroundRemovalResult {
  width: number;
  height: number;
  evidence: Float32Array | null;
}

interface PixelGrid {
  cellSize: number;
  width: number;
  height: number;
  red: Float32Array;
  green: Float32Array;
  blue: Float32Array;
  luminance: Float32Array;
  deviation: Float32Array;
}

const rgbLuminance = (red: number, green: number, blue: number): number => red * .2126 + green * .7152 + blue * .0722;
const dataLuminance = (data: Uint8ClampedArray, index: number): number => rgbLuminance(data[index], data[index + 1], data[index + 2]);
const rgbSpread = (red: number, green: number, blue: number): number => Math.max(red, green, blue) - Math.min(red, green, blue);
const smoothstep = (from: number, to: number, value: number): number => {
  const position = Math.max(0, Math.min(1, (value - from) / Math.max(1, to - from)));
  return position * position * (3 - 2 * position);
};

function colorDistance(
  red: number, green: number, blue: number,
  backgroundRed: number, backgroundGreen: number, backgroundBlue: number,
): number {
  return Math.sqrt(
    (red - backgroundRed) ** 2 * .26
    + (green - backgroundGreen) ** 2 * .55
    + (blue - backgroundBlue) ** 2 * .19,
  );
}

function fastColorDifference(
  red: number, green: number, blue: number,
  otherRed: number, otherGreen: number, otherBlue: number,
): number {
  return Math.abs(red - otherRed) * .26 + Math.abs(green - otherGreen) * .55 + Math.abs(blue - otherBlue) * .19;
}

function buildPixelGrid(data: Uint8ClampedArray, width: number, height: number): PixelGrid {
  const cellSize = Math.max(1, Math.ceil(Math.max(width, height) / 96));
  const gridWidth = Math.ceil(width / cellSize);
  const gridHeight = Math.ceil(height / cellSize);
  const length = gridWidth * gridHeight;
  const red = new Float32Array(length);
  const green = new Float32Array(length);
  const blue = new Float32Array(length);
  const luminance = new Float32Array(length);
  const luminanceSquared = new Float64Array(length);
  const counts = new Uint32Array(length);

  for (let y = 0; y < height; y += 1) {
    const gridY = Math.floor(y / cellSize);
    for (let x = 0; x < width; x += 1) {
      const pixel = (y * width + x) * 4;
      if (data[pixel + 3] === 0) continue;
      const cell = gridY * gridWidth + Math.floor(x / cellSize);
      const pixelLuminance = rgbLuminance(data[pixel], data[pixel + 1], data[pixel + 2]);
      red[cell] += data[pixel];
      green[cell] += data[pixel + 1];
      blue[cell] += data[pixel + 2];
      luminance[cell] += pixelLuminance;
      luminanceSquared[cell] += pixelLuminance * pixelLuminance;
      counts[cell] += 1;
    }
  }

  const deviation = new Float32Array(length);
  for (let index = 0; index < length; index += 1) {
    const count = Math.max(1, counts[index]);
    red[index] /= count;
    green[index] /= count;
    blue[index] /= count;
    luminance[index] /= count;
    deviation[index] = Math.sqrt(Math.max(0, luminanceSquared[index] / count - luminance[index] ** 2));
  }
  return { cellSize, width: gridWidth, height: gridHeight, red, green, blue, luminance, deviation };
}

function blurredGrid(grid: PixelGrid, passes = 2): Pick<PixelGrid, 'red' | 'green' | 'blue'> {
  let red = grid.red.slice();
  let green = grid.green.slice();
  let blue = grid.blue.slice();
  const length = grid.width * grid.height;
  for (let pass = 0; pass < passes; pass += 1) {
    const nextRed = new Float32Array(length);
    const nextGreen = new Float32Array(length);
    const nextBlue = new Float32Array(length);
    for (let y = 0; y < grid.height; y += 1) {
      for (let x = 0; x < grid.width; x += 1) {
        let sumRed = 0;
        let sumGreen = 0;
        let sumBlue = 0;
        let count = 0;
        for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
          const sampleY = y + offsetY;
          if (sampleY < 0 || sampleY >= grid.height) continue;
          for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
            const sampleX = x + offsetX;
            if (sampleX < 0 || sampleX >= grid.width) continue;
            const sample = sampleY * grid.width + sampleX;
            sumRed += red[sample];
            sumGreen += green[sample];
            sumBlue += blue[sample];
            count += 1;
          }
        }
        const target = y * grid.width + x;
        nextRed[target] = sumRed / count;
        nextGreen[target] = sumGreen / count;
        nextBlue[target] = sumBlue / count;
      }
    }
    red = nextRed;
    green = nextGreen;
    blue = nextBlue;
  }
  return { red, green, blue };
}

function paperBackgroundGrid(grid: PixelGrid): {
  red: Float32Array; green: Float32Array; blue: Float32Array; borderBackground: Uint8Array;
} | null {
  const length = grid.width * grid.height;
  const candidates = new Uint8Array(length);
  const minimumPaperLuminance = 172;
  let candidateCount = 0;
  for (let index = 0; index < length; index += 1) {
    const neutral = rgbSpread(grid.red[index], grid.green[index], grid.blue[index]) <= 38;
    if (grid.luminance[index] >= minimumPaperLuminance && neutral && grid.deviation[index] <= 24) {
      candidates[index] = 1;
      candidateCount += 1;
    }
  }
  if (candidateCount < Math.max(2, Math.round(length * .01))) return null;

  const red = new Float32Array(length);
  const green = new Float32Array(length);
  const blue = new Float32Array(length);
  const visited = new Uint8Array(length);
  const queue = new Int32Array(length);
  let head = 0;
  let tail = 0;
  for (let index = 0; index < length; index += 1) {
    if (!candidates[index]) continue;
    red[index] = grid.red[index];
    green[index] = grid.green[index];
    blue[index] = grid.blue[index];
    visited[index] = 1;
    queue[tail++] = index;
  }
  const enqueue = (from: number, target: number) => {
    if (visited[target]) return;
    visited[target] = 1;
    red[target] = red[from];
    green[target] = green[from];
    blue[target] = blue[from];
    queue[tail++] = target;
  };
  while (head < tail) {
    const index = queue[head++];
    const x = index % grid.width;
    const y = Math.floor(index / grid.width);
    if (x > 0) enqueue(index, index - 1);
    if (x + 1 < grid.width) enqueue(index, index + 1);
    if (y > 0) enqueue(index, index - grid.width);
    if (y + 1 < grid.height) enqueue(index, index + grid.width);
  }

  const paperGrid: PixelGrid = { ...grid, red, green, blue };
  const smoothed = blurredGrid(paperGrid, 2);
  const borderBackground = connectedSmoothBorder(grid, candidates);
  return { ...smoothed, borderBackground };
}

function connectedSmoothBorder(grid: PixelGrid, paperCandidates: Uint8Array): Uint8Array {
  const length = grid.width * grid.height;
  const result = new Uint8Array(length);
  const queue = new Int32Array(length);
  let head = 0;
  let tail = 0;
  const seed = (index: number) => {
    if (result[index] || grid.deviation[index] > 28) return;
    const spread = rgbSpread(grid.red[index], grid.green[index], grid.blue[index]);
    if (!paperCandidates[index] && grid.luminance[index] >= 145 && spread >= 30) return;
    result[index] = 1;
    queue[tail++] = index;
  };
  for (let x = 0; x < grid.width; x += 1) {
    seed(x);
    seed((grid.height - 1) * grid.width + x);
  }
  for (let y = 1; y + 1 < grid.height; y += 1) {
    seed(y * grid.width);
    seed(y * grid.width + grid.width - 1);
  }

  const visit = (from: number, target: number) => {
    if (result[target] || grid.deviation[target] > 28) return;
    const difference = colorDistance(
      grid.red[from], grid.green[from], grid.blue[from],
      grid.red[target], grid.green[target], grid.blue[target],
    );
    const bothPaper = paperCandidates[from] && paperCandidates[target];
    const bothDark = grid.luminance[from] < 135 && grid.luminance[target] < 135;
    if (difference > (bothPaper ? 68 : bothDark ? 46 : 34)) return;
    result[target] = 1;
    queue[tail++] = target;
  };
  while (head < tail) {
    const index = queue[head++];
    const x = index % grid.width;
    const y = Math.floor(index / grid.width);
    if (x > 0) visit(index, index - 1);
    if (x + 1 < grid.width) visit(index, index + 1);
    if (y > 0) visit(index, index - grid.width);
    if (y + 1 < grid.height) visit(index, index + grid.width);
  }
  return result;
}

function sampleGrid(values: Float32Array, grid: PixelGrid, x: number, y: number): number {
  const gridX = Math.max(0, Math.min(grid.width - 1, x / grid.cellSize - .5));
  const gridY = Math.max(0, Math.min(grid.height - 1, y / grid.cellSize - .5));
  const left = Math.floor(gridX);
  const top = Math.floor(gridY);
  const right = Math.min(grid.width - 1, left + 1);
  const bottom = Math.min(grid.height - 1, top + 1);
  const horizontal = gridX - left;
  const vertical = gridY - top;
  const topValue = values[top * grid.width + left] * (1 - horizontal) + values[top * grid.width + right] * horizontal;
  const bottomValue = values[bottom * grid.width + left] * (1 - horizontal) + values[bottom * grid.width + right] * horizontal;
  return topValue * (1 - vertical) + bottomValue * vertical;
}

function borderWeight(mask: Uint8Array, grid: PixelGrid, x: number, y: number): number {
  const centerX = Math.max(0, Math.min(grid.width - 1, Math.floor(x / grid.cellSize)));
  const centerY = Math.max(0, Math.min(grid.height - 1, Math.floor(y / grid.cellSize)));
  let sum = 0;
  let count = 0;
  for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
    const sampleY = centerY + offsetY;
    if (sampleY < 0 || sampleY >= grid.height) continue;
    for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
      const sampleX = centerX + offsetX;
      if (sampleX < 0 || sampleX >= grid.width) continue;
      sum += mask[sampleY * grid.width + sampleX];
      count += 1;
    }
  }
  return sum / count;
}

function localEdge(data: Uint8ClampedArray, width: number, height: number, x: number, y: number, luminance: number): number {
  let edge = 0;
  const center = (y * width + x) * 4;
  if (x > 0) edge = Math.max(edge, Math.abs(luminance - dataLuminance(data, center - 4)));
  if (x + 1 < width) edge = Math.max(edge, Math.abs(luminance - dataLuminance(data, center + 4)));
  if (y > 0) edge = Math.max(edge, Math.abs(luminance - dataLuminance(data, center - width * 4)));
  if (y + 1 < height) edge = Math.max(edge, Math.abs(luminance - dataLuminance(data, center + width * 4)));
  return edge;
}

function ridgeEvidence(data: Uint8ClampedArray, width: number, height: number, x: number, y: number, photoReference = false): number {
  const center = (y * width + x) * 4;
  const centerRed = data[center];
  const centerGreen = data[center + 1];
  const centerBlue = data[center + 2];
  const centerLuminance = rgbLuminance(centerRed, centerGreen, centerBlue);
  const scaledRadius = Math.max(3, Math.min(6, Math.round(Math.max(width, height) / 450)));
  const radii = [1, scaledRadius];
  const directions = [[1, 0], [0, 1], [1, 1], [1, -1]] as const;
  let strongest = 0;
  for (const radius of radii) {
    for (const [directionX, directionY] of directions) {
      const firstX = x - directionX * radius;
      const firstY = y - directionY * radius;
      const secondX = x + directionX * radius;
      const secondY = y + directionY * radius;
      if (firstX < 0 || firstX >= width || firstY < 0 || firstY >= height
        || secondX < 0 || secondX >= width || secondY < 0 || secondY >= height) continue;
      const first = (firstY * width + firstX) * 4;
      const second = (secondY * width + secondX) * 4;
      const centerToFirst = fastColorDifference(centerRed, centerGreen, centerBlue, data[first], data[first + 1], data[first + 2]);
      const centerToSecond = fastColorDifference(centerRed, centerGreen, centerBlue, data[second], data[second + 1], data[second + 2]);
      const sideDistance = fastColorDifference(data[first], data[first + 1], data[first + 2], data[second], data[second + 1], data[second + 2]);
      const firstLuminance = dataLuminance(data, first);
      const secondLuminance = dataLuminance(data, second);
      const sidePenalty = photoReference ? 1 : .42;
      const colorRidge = Math.min(centerToFirst, centerToSecond) - sideDistance * sidePenalty;
      const luminanceRidge = Math.min(
        Math.abs(centerLuminance - firstLuminance),
        Math.abs(centerLuminance - secondLuminance),
      ) - Math.abs(firstLuminance - secondLuminance) * (photoReference ? 1 : .48);
      const centerChroma = centerBlue - centerRed;
      const firstChroma = data[first + 2] - data[first];
      const secondChroma = data[second + 2] - data[second];
      const chromaRidge = photoReference
        ? Math.min(Math.abs(centerChroma - firstChroma), Math.abs(centerChroma - secondChroma)) * .6
          - Math.abs(firstChroma - secondChroma) * .6
        : 0;
      strongest = Math.max(strongest, colorRidge, luminanceRidge, chromaRidge);
    }
  }
  return Math.max(0, strongest);
}

function gridDetailDensity(grid: PixelGrid): Float32Array {
  const density = new Float32Array(grid.width * grid.height);
  for (let y = 0; y < grid.height; y += 1) {
    for (let x = 0; x < grid.width; x += 1) {
      let detailed = 0;
      let count = 0;
      for (let offsetY = -2; offsetY <= 2; offsetY += 1) {
        const sampleY = y + offsetY;
        if (sampleY < 0 || sampleY >= grid.height) continue;
        for (let offsetX = -2; offsetX <= 2; offsetX += 1) {
          const sampleX = x + offsetX;
          if (sampleX < 0 || sampleX >= grid.width) continue;
          if (grid.deviation[sampleY * grid.width + sampleX] > 22) detailed += 1;
          count += 1;
        }
      }
      density[y * grid.width + x] = detailed / count;
    }
  }
  return density;
}

function betweenDistinctBackgroundRegions(mask: Uint8Array, grid: PixelGrid, x: number, y: number): boolean {
  const centerX = Math.max(0, Math.min(grid.width - 1, Math.floor(x / grid.cellSize)));
  const centerY = Math.max(0, Math.min(grid.height - 1, Math.floor(y / grid.cellSize)));
  const directions = [[1, 0], [0, 1], [1, 1], [1, -1]] as const;
  for (const [directionX, directionY] of directions) {
    for (let radius = 1; radius <= 2; radius += 1) {
      const firstX = centerX - directionX * radius;
      const firstY = centerY - directionY * radius;
      const secondX = centerX + directionX * radius;
      const secondY = centerY + directionY * radius;
      if (firstX < 0 || firstX >= grid.width || firstY < 0 || firstY >= grid.height
        || secondX < 0 || secondX >= grid.width || secondY < 0 || secondY >= grid.height) continue;
      const first = firstY * grid.width + firstX;
      const second = secondY * grid.width + secondX;
      if (!mask[first] || !mask[second]) continue;
      if (colorDistance(
        grid.red[first], grid.green[first], grid.blue[first],
        grid.red[second], grid.green[second], grid.blue[second],
      ) > 28) return true;
    }
  }
  return false;
}

function applyCleanLightRemoval(data: Uint8ClampedArray, threshold: number): void {
  for (let index = 0; index < data.length; index += 4) {
    const red = data[index];
    const green = data[index + 1];
    const blue = data[index + 2];
    const minimum = Math.min(red, green, blue);
    const spread = Math.max(red, green, blue) - minimum;
    if (minimum < threshold || spread >= 42) continue;
    const distanceFromWhite = 255 - minimum;
    data[index + 3] = Math.round(data[index + 3] * Math.min(1, Math.max(0, distanceFromWhite / Math.max(1, 255 - threshold))));
  }
}

function buildAdaptiveEvidence(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  grid: PixelGrid,
  paper: ReturnType<typeof paperBackgroundGrid>,
): Float32Array {
  const evidenceMap = new Float32Array(width * height);
  const detailDensity = paper ? gridDetailDensity(grid) : null;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      if (!data[index + 3]) continue;
      if (!paper) {
        const margin = Math.min(x, y, width - 1 - x, height - 1 - y);
        const marginWeight = .35 + .65 * Math.min(1, margin / Math.max(12, Math.min(width, height) * .025));
        evidenceMap[index / 4] = ridgeEvidence(data, width, height, x, y, true) * 2.2 * marginWeight;
        continue;
      }
      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];
      const pixelLuminance = rgbLuminance(red, green, blue);
      const backgroundRed = sampleGrid(paper.red, grid, x, y);
      const backgroundGreen = sampleGrid(paper.green, grid, x, y);
      const backgroundBlue = sampleGrid(paper.blue, grid, x, y);
      const difference = colorDistance(red, green, blue, backgroundRed, backgroundGreen, backgroundBlue);
      const edge = localEdge(data, width, height, x, y, pixelLuminance);
      let evidence = Math.max(difference, edge * 1.45);
      if (paper.borderBackground) {
        const gridX = Math.max(0, Math.min(grid.width - 1, Math.floor(x / grid.cellSize)));
        const gridY = Math.max(0, Math.min(grid.height - 1, Math.floor(y / grid.cellSize)));
        const cell = gridY * grid.width + gridX;
        const directlyConnected = paper.borderBackground[cell] === 1;
        const sparseBackground = detailDensity![cell] < .34;
        const sparseBoundary = sparseBackground
          && borderWeight(paper.borderBackground, grid, x, y) >= .45
          && betweenDistinctBackgroundRegions(paper.borderBackground, grid, x, y);
        if ((directlyConnected && sparseBackground) || sparseBoundary) evidence = 0;
        else if (directlyConnected) evidence = Math.min(evidence, ridgeEvidence(data, width, height, x, y) * 1.8);
      }
      evidenceMap[index / 4] = evidence;
    }
  }
  return evidenceMap;
}

function removeSmallPerimeterEvidence(evidence: Float32Array, width: number, height: number): void {
  const visited = new Uint8Array(evidence.length);
  const queue = new Int32Array(evidence.length);
  const maximumBackgroundFragment = Math.max(64, Math.round(evidence.length * .012));
  let tail = 0;
  const inspect = (seed: number) => {
    if (visited[seed] || evidence[seed] < 8) return;
    const start = tail;
    visited[seed] = 1;
    queue[tail++] = seed;
    let head = start;
    while (head < tail) {
      const pixel = queue[head++];
      const x = pixel % width;
      if (x > 0) enqueueEvidence(pixel - 1);
      if (x + 1 < width) enqueueEvidence(pixel + 1);
      if (pixel >= width) enqueueEvidence(pixel - width);
      if (pixel + width < evidence.length) enqueueEvidence(pixel + width);
    }
    if (tail - start >= maximumBackgroundFragment) return;
    for (let index = start; index < tail; index += 1) evidence[queue[index]] = 0;
  };
  const enqueueEvidence = (pixel: number) => {
    if (visited[pixel] || evidence[pixel] < 8) return;
    visited[pixel] = 1;
    queue[tail++] = pixel;
  };
  for (let x = 0; x < width; x += 1) {
    inspect(x);
    inspect((height - 1) * width + x);
  }
  for (let y = 1; y + 1 < height; y += 1) {
    inspect(y * width);
    inspect(y * width + width - 1);
  }
}

function removeShallowDarkPerimeterFragments(
  data: Uint8ClampedArray, evidence: Float32Array, width: number, height: number, cellSize: number,
): void {
  const band = Math.min(height, cellSize);
  const maximumRun = Math.max(3, Math.floor(width * .08));
  const visited = new Uint8Array(width * band);
  const queue = new Int32Array(width * band);
  const backgroundLike = (pixel: number, maximumLuminance: number): boolean => {
    if (evidence[pixel] < 8) return false;
    const index = pixel * 4;
    return dataLuminance(data, index) < maximumLuminance
      && rgbSpread(data[index], data[index + 1], data[index + 2]) < 35;
  };
  for (let x = 0; x < width;) {
    if (!backgroundLike(x, 155)) { x += 1; continue; }
    const first = x;
    while (x < width && backgroundLike(x, 155)) x += 1;
    const last = x - 1;
    if (first === 0 || x === width || last - first + 1 > maximumRun) continue;
    const joinsRemovedDarkBorder = [first - 1, x].some((neighbor) => {
      const edge = neighbor < first ? first : last;
      const source = neighbor * 4;
      const boundary = edge * 4;
      return evidence[neighbor] < 8 && dataLuminance(data, source) < 100
        && colorDistance(
          data[source], data[source + 1], data[source + 2],
          data[boundary], data[boundary + 1], data[boundary + 2],
        ) < 30;
    });
    if (!joinsRemovedDarkBorder) continue;
    let head = 0;
    let tail = 0;
    for (let seed = first; seed <= last; seed += 1) {
      visited[seed] = 1;
      queue[tail++] = seed;
    }
    const visit = (pixel: number) => {
      if (pixel < 0 || pixel >= width * band || visited[pixel] || !backgroundLike(pixel, 180)) return;
      const column = pixel % width;
      if (column < first - 2 || column > last + 2) return;
      visited[pixel] = 1;
      queue[tail++] = pixel;
    };
    while (head < tail) {
      const pixel = queue[head++];
      const column = pixel % width;
      if (column > 0) visit(pixel - 1);
      if (column + 1 < width) visit(pixel + 1);
      visit(pixel + width);
      // Growth starts at the image perimeter and stays in this shallow band.
      if (pixel >= width) visit(pixel - width);
    }
    if (tail > maximumRun * band) continue;
    for (let index = 0; index < tail; index += 1) evidence[queue[index]] = 0;
  }
}

function suppressWeakPhotoComponents(evidence: Float32Array, width: number, height: number): void {
  const visited = new Uint8Array(evidence.length);
  const minimumSpan = Math.max(width, height) * .045;
  for (let seed = 0; seed < evidence.length; seed += 1) {
    if (visited[seed] || evidence[seed] < 21) continue;
    const component = [seed];
    visited[seed] = 1;
    let strength = 0;
    let minX = width;
    let maxX = 0;
    let minY = height;
    let maxY = 0;
    let perimeterPixels = 0;
    for (let head = 0; head < component.length; head += 1) {
      const pixel = component[head];
      const x = pixel % width;
      const y = Math.floor(pixel / width);
      strength += evidence[pixel];
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      if (x < 30 || x >= width - 30 || y < 30 || y >= height - 30) perimeterPixels += 1;
      for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
        const nextY = y + offsetY;
        if (nextY < 0 || nextY >= height) continue;
        for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
          const nextX = x + offsetX;
          if (nextX < 0 || nextX >= width) continue;
          const next = nextY * width + nextX;
          if (visited[next] || evidence[next] < 21) continue;
          visited[next] = 1;
          component.push(next);
        }
      }
    }
    const meanStrength = strength / component.length;
    const span = Math.max(maxX - minX + 1, maxY - minY + 1);
    const weakLongBoundary = meanStrength < 65 && (span >= minimumSpan || component.length >= 200);
    const weakShortBoundary = meanStrength < 55 && component.length >= 60;
    const perimeterBoundary = meanStrength < 70 && perimeterPixels >= Math.max(4, component.length * .1);
    if (!weakLongBoundary && !weakShortBoundary && !perimeterBoundary) continue;
    for (const pixel of component) evidence[pixel] *= .25;
  }
}

/**
 * Removes a design background without uploading pixels. A uniform white image
 * keeps the old fast threshold path. Uneven neutral paper uses a coarse local
 * paper model plus smooth perimeter regions; other photos use a conservative
 * high-frequency/line extraction fallback. RGB is left intact and only alpha
 * changes, so coloured artwork is preserved for the normal editor renderer.
 */
export function analyzeArtworkBackground(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): ArtworkBackgroundAnalysis {
  if (width < 1 || height < 1 || width * height * 4 !== data.length) {
    return { mode: 'clean_light', neutralLightRatio: 0, width, height, evidence: null };
  }
  let visible = 0;
  let nonOpaque = 0;
  let neutralLight = 0;
  let veryLight = 0;
  for (let index = 0; index < data.length; index += 4) {
    if (data[index + 3] < 245) nonOpaque += 1;
    if (!data[index + 3]) continue;
    visible += 1;
    const red = data[index];
    const green = data[index + 1];
    const blue = data[index + 2];
    const luminance = rgbLuminance(red, green, blue);
    const spread = rgbSpread(red, green, blue);
    if (luminance >= 180 && spread < 35) neutralLight += 1;
    if (luminance >= 228 && spread < 28) veryLight += 1;
  }
  const neutralLightRatio = neutralLight / Math.max(1, visible);
  const veryLightRatio = veryLight / Math.max(1, visible);
  if (nonOpaque / (width * height) > .001) {
    return { mode: 'preserve_alpha', neutralLightRatio, width, height, evidence: null };
  }
  if (neutralLightRatio >= .62 && veryLightRatio >= .52) {
    return { mode: 'clean_light', neutralLightRatio, width, height, evidence: null };
  }

  const grid = buildPixelGrid(data, width, height);
  if (neutralLightRatio >= .16) {
    const paper = paperBackgroundGrid(grid);
    if (paper) {
      const evidence = buildAdaptiveEvidence(data, width, height, grid, paper);
      removeSmallPerimeterEvidence(evidence, width, height);
      removeShallowDarkPerimeterFragments(data, evidence, width, height, grid.cellSize);
      return {
        mode: 'adaptive_paper', neutralLightRatio, width, height,
        evidence,
      };
    }
  }
  const evidence = buildAdaptiveEvidence(data, width, height, grid, null);
  suppressWeakPhotoComponents(evidence, width, height);
  return {
    mode: 'line_extraction', neutralLightRatio, width, height,
    evidence,
  };
}

export function applyArtworkBackgroundAnalysis(
  data: Uint8ClampedArray,
  threshold: number,
  analysis: ArtworkBackgroundAnalysis,
): ArtworkBackgroundRemovalResult {
  if (analysis.width * analysis.height * 4 !== data.length || analysis.mode === 'clean_light') {
    applyCleanLightRemoval(data, Math.max(180, Math.min(255, threshold)));
    return { mode: analysis.mode, neutralLightRatio: analysis.neutralLightRatio };
  }
  if (analysis.mode === 'preserve_alpha' || !analysis.evidence) {
    return { mode: analysis.mode, neutralLightRatio: analysis.neutralLightRatio };
  }
  const sensitivity = (255 - Math.max(180, Math.min(255, threshold))) / 75;
  const low = 8 + sensitivity * 22;
  const high = 30 + sensitivity * 45;
  for (let index = 3; index < data.length; index += 4) {
    data[index] = Math.round(data[index] * smoothstep(low, high, analysis.evidence[(index - 3) / 4]));
  }
  return { mode: analysis.mode, neutralLightRatio: analysis.neutralLightRatio };
}

export function removeArtworkBackground(
  data: Uint8ClampedArray,
  threshold: number,
  width: number,
  height: number,
): ArtworkBackgroundRemovalResult {
  const analysis = analyzeArtworkBackground(data, width, height);
  return applyArtworkBackgroundAnalysis(data, threshold, analysis);
}

export function applyLegacyWhiteRemoval(data: Uint8ClampedArray, threshold: number): void {
  applyCleanLightRemoval(data, Math.max(180, Math.min(255, threshold)));
}
